import { useEffect, useState } from "react";
import { Settings2, Save, Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PanelHeader, Callout } from "@/components/settings/SettingsUI";
import { useAuth } from "@/auth/AuthContext";
import { getFoundationSettings, putFoundationSetting, getFoundationSettingsAudit, getFoundationJobSpPreflight, getFoundationRefStatus, getFoundationBridgeUpdateStatus, runFoundationBridgeSelfUpdate } from "@/lib/api";
import FoundationARSettings from "./FoundationARSettings";
import FoundationRefPicker from "./FoundationRefPicker";

const CONSTANT_KEYS = ["division_account_no", "division2_account_no", "dept_no", "cost_basis"];
// Each constant has its own on/off send toggle.
const SEND_TOGGLE = {
  division_account_no: "send_division", division2_account_no: "send_division2",
  dept_no: "send_dept", cost_basis: "send_cost_basis",
};
const TAX_LABELS = { neither: "Send neither", ap: "Send @tax_ap_no", ar: "Send @tax_ar_no", both: "Send both" };

function Row({ label, note, children, testId }) {
  return (
    <div className="grid grid-cols-12 gap-3 items-start py-2 border-b border-slate-100 last:border-b-0" data-testid={testId}>
      <div className="col-span-4">
        <div className="text-xs font-semibold text-slate-700 font-mono">{label}</div>
        <div className="text-[11px] text-slate-500 leading-snug">{note}</div>
      </div>
      <div className="col-span-8 flex items-center gap-2 flex-wrap">{children}</div>
    </div>
  );
}

export default function FoundationSettingsPanel() {
  const { can } = useAuth();
  const canWrite = can("integrations.foundation_queue", "write");
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(null);
  const [audit, setAudit] = useState([]);
  const [preflight, setPreflight] = useState(null);
  const [refTables, setRefTables] = useState([]);
  const [dbConfirm, setDbConfirm] = useState("");
  const [bridge, setBridge] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [updateMsg, setUpdateMsg] = useState("");

  const loadBridge = () => getFoundationBridgeUpdateStatus()
    .then(r => setBridge(r.data)).catch(() => setBridge(null));

  const checkBridgeUpdate = async () => {
    setUpdating(true);
    setUpdateMsg("");
    try {
      const { data } = await runFoundationBridgeSelfUpdate();
      const m = data.result?.message || "Bridge update finished";
      setUpdateMsg(`${m} Now running ${data.running_version || "unknown"} (was ${data.previous_version || "?"}).`);
      if (data.result?.updated) toast.success(m); else toast.info(m);
      await Promise.all([loadBridge(), load()]);
    } catch (e) {
      const status = e?.response?.status;
      const raw = e?.response?.data;
      const d = raw?.detail
        || (typeof raw === "string" && /cloudflare|origin web server|bad gateway/i.test(raw)
          ? `Proxy error (HTTP ${status}) before the request reached ServiceOps — a deployment is probably still rolling out. Wait a minute and try again.`
          : `Bridge update failed${status ? ` (HTTP ${status})` : ""}`);
      setUpdateMsg(d);
      toast.error(d);
    } finally { setUpdating(false); }
  };

  const load = async () => {
    const [s, a, p, r] = await Promise.all([getFoundationSettings(), getFoundationSettingsAudit(40), getFoundationJobSpPreflight().catch(() => null), getFoundationRefStatus().catch(() => null)]);
    setData(s.data); setDraft(s.data.values); setAudit(a.data); setPreflight(p?.data || null); setRefTables(r?.data?.lists || []);
  };
  useEffect(() => { load().catch(() => toast.error("Could not load Foundation settings")); loadBridge(); }, []);

  const save = async (key, value, confirmText) => {
    setSaving(key);
    try {
      await putFoundationSetting(key, value, confirmText);
      toast.success(`${data.spec[key].label} saved`);
      await load();
      setDbConfirm("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(null); }
  };

  if (!data) return <div className="p-5 text-sm text-slate-500" data-testid="foundation-settings-loading">Loading…</div>;
  const { spec, values } = data;
  const connectedDb = preflight?.database;
  const dbOk = connectedDb && connectedDb === values.required_database;

  return (
    <div data-testid="foundation-settings-panel">
      <PanelHeader icon={Settings2} title="Foundation Settings" hint="Constants and switches for the jobs push — configuration, never code" testId="foundation-settings-header"
        actions={(
          <div className="flex items-center gap-2 text-xs">
            <span className="text-slate-500">Target database</span>
            <code className={`px-2 py-0.5 rounded font-semibold ${dbOk ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`} data-testid="foundation-target-db-badge">
              {values.required_database}{connectedDb ? ` · bridge: ${connectedDb}` : " · bridge offline"}
            </code>
            {preflight?.bridge_version && <span className="text-slate-400" data-testid="foundation-bridge-version">bridge v{preflight.bridge_version}{preflight.schema_capable ? "" : " (pre-2.3)"}</span>}
          </div>
        )} />
      <div className="px-5 pb-5 space-y-5">
        <Callout>Every value below is sent on each push exactly as shown. Blank / zero means the parameter is omitted (Foundation default). <code>@job_status</code> and <code>@terms_no</code> are never sent. Each change is logged with who / when / old / new.</Callout>

        {/* Bridge version + self-update. Manual only — the bridge never
            upgrades itself unattended. */}
        <section className="rounded-md border border-slate-200 p-3" data-testid="fs-bridge-update">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="text-xs">
              <div className="font-semibold text-slate-700 uppercase tracking-wide mb-0.5">On-prem bridge</div>
              <div className="text-slate-600">
                Running <code className="font-semibold">{bridge?.running_version || (bridge?.reachable === false ? "offline" : "…")}</code>
                {" · "}available in ServiceOps <code className="font-semibold">{bridge?.available_version || "?"}</code>
                {bridge?.update_available && <span className="ml-1 text-amber-800 font-semibold">update available</span>}
                {bridge && !bridge.update_available && bridge.running_version && <span className="ml-1 text-emerald-700 font-semibold">up to date</span>}
              </div>
              <div className="text-[11px] text-slate-400 mt-0.5">
                {bridge?.self_update_capable
                  ? "The bridge can pull, verify and install its own updates — no server visit needed."
                  : "This bridge has no self-update endpoint yet; deploy docs/bridge_server.py by hand once (BRIDGE_V2.3_DEPLOY.md) and every upgrade after that is one click."}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/integrations/foundation/bridge-file`} download
                 className="inline-flex items-center h-8 px-3 rounded-md border border-slate-300 text-xs hover:bg-slate-50" data-testid="fs-bridge-download-link"
                 title="Download bridge_server.py to hand to IT (same bytes the self-update installs)">
                <Download className="w-3 h-3 mr-1" /> Download bridge_server.py{bridge?.available_version ? ` (v${bridge.available_version})` : ""}
              </a>
              <Button size="sm" variant="outline" className="h-8 text-xs" disabled={!canWrite || updating}
                      onClick={checkBridgeUpdate} data-testid="fs-bridge-update-btn">
                {updating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
                Check for bridge update
              </Button>
            </div>
          </div>
          {updateMsg && <div className="text-[11px] mt-2 text-slate-600" data-testid="fs-bridge-update-result">{updateMsg}</div>}
        </section>

        <section>
          <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1">Constants (B1)</div>
          {CONSTANT_KEYS.map(k => (
            <Row key={k} label={spec[k].label} note={spec[k].note} testId={`fs-row-${k}`}>
              <Input value={draft[k] ?? ""} onChange={e => setDraft(d => ({ ...d, [k]: e.target.value }))} disabled={!canWrite || !values[SEND_TOGGLE[k]]}
                     className="h-8 w-36 font-mono text-xs" maxLength={spec[k].max || 12} data-testid={`fs-input-${k}`} />
              <Button size="sm" variant="outline" className="h-8" disabled={!canWrite || saving === k || String(draft[k]) === String(values[k])}
                      onClick={() => save(k, draft[k])} data-testid={`fs-save-${k}`}>
                {saving === k ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              </Button>
              <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer">
                <input type="checkbox" checked={!!values[SEND_TOGGLE[k]]} disabled={!canWrite || saving === SEND_TOGGLE[k]}
                       onChange={e => save(SEND_TOGGLE[k], e.target.checked)}
                       className="h-3.5 w-3.5 rounded border-slate-300 accent-violet-600"
                       data-testid={`fs-toggle-${SEND_TOGGLE[k]}`} />
                {values[SEND_TOGGLE[k]] ? "Sent on every push" : "Not sent (omitted)"}
              </label>
            </Row>
          ))}
        </section>

        <section>
          <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1">Switches</div>
          {data.rules?.length > 0 && (
            <div className="mb-2 rounded-md border border-slate-200 bg-slate-50 p-2 space-y-1" data-testid="fs-rules">
              {data.rules.map(r => (
                <div key={r.key} className="text-[11px] text-slate-700" data-testid={`fs-rule-${r.key}`}>
                  <span className="font-mono font-semibold text-slate-900">@{r.key === "tax" ? "tax_ap_no / @tax_ar_no" : r.key}</span> — {r.text}
                </div>
              ))}
            </div>
          )}
          <Row label={spec.tax_mode.label} note={spec.tax_mode.note} testId="fs-row-tax_mode">
            <div className="flex rounded-md border border-slate-200 overflow-hidden" data-testid="fs-tax-mode">
              {data.tax_modes.map(m => (
                <button key={m} type="button" disabled={!canWrite} onClick={() => save("tax_mode", m)}
                        className={`px-2.5 h-8 text-xs border-r last:border-r-0 border-slate-200 ${values.tax_mode === m ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                        data-testid={`fs-tax-mode-${m}`}>{TAX_LABELS[m]}</button>
              ))}
            </div>
          </Row>
          <Row label={spec.contract_amount_source.label} note={spec.contract_amount_source.note} testId="fs-row-contract_amount_source">
            <div className="flex rounded-md border border-slate-200 overflow-hidden">
              {["subtotal", "total"].map(m => (
                <button key={m} type="button" disabled={!canWrite} onClick={() => save("contract_amount_source", m)}
                        className={`px-2.5 h-8 text-xs border-r last:border-r-0 border-slate-200 ${values.contract_amount_source === m ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                        data-testid={`fs-contract-${m}`}>{m === "subtotal" ? "Subtotal (pre-tax)" : "Total (incl. tax) — accounting-confirmed"}</button>
              ))}
            </div>
          </Row>
          <Row label={spec.hold_completion_until_budget_exported.label} note={spec.hold_completion_until_budget_exported.note} testId="fs-row-hold_completion">
            <div className="flex rounded-md border border-slate-200 overflow-hidden" data-testid="fs-hold-completion">
              {[false, true].map(v => (
                <button key={String(v)} type="button" disabled={!canWrite} onClick={() => save("hold_completion_until_budget_exported", v)}
                        className={`px-2.5 h-8 text-xs border-r last:border-r-0 border-slate-200 ${values.hold_completion_until_budget_exported === v ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                        data-testid={`fs-hold-completion-${v ? "on" : "off"}`}>{v ? "ON — omit the date until exported" : "OFF — block and ask"}</button>
              ))}
            </div>
          </Row>
          <Row label={spec.required_database.label} note={spec.required_database.note} testId="fs-row-required_database">
            {values.required_database === data.practice_database ? (
              <div className="flex items-center gap-2 flex-wrap">
                <Input value={dbConfirm} onChange={e => setDbConfirm(e.target.value)} disabled={!canWrite}
                       placeholder={`Type ${data.live_database} to switch to live`} className="h-8 w-64 font-mono text-xs" data-testid="fs-db-confirm-input" />
                <Button size="sm" variant="destructive" className="h-8 text-xs" disabled={!canWrite || dbConfirm !== data.live_database || saving === "required_database"}
                        onClick={() => save("required_database", data.live_database, dbConfirm)} data-testid="fs-db-switch-live">Switch to Live</Button>
              </div>
            ) : (
              <Button size="sm" variant="outline" className="h-8 text-xs" disabled={!canWrite} onClick={() => save("required_database", data.practice_database)} data-testid="fs-db-switch-practice">Back to Practice</Button>
            )}
          </Row>
        </section>

        <section data-testid="fs-po-co-sync">
          <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1">PO / CO sync rows (Practice)</div>
          {spec.po_sync_shape && (
            <Row label={spec.po_sync_shape.label} note={spec.po_sync_shape.note} testId="fs-row-po_sync_shape">
              <div className="flex rounded-md border border-slate-200 overflow-hidden" data-testid="fs-po-sync-shape">
                {[["tom_p", "Detail — po_format D, G/L per class, divisions, no item_no"], ["full", "Detail + description"], ["tom_minimal", "Basic — Tom's columns only"]].map(([m, lbl]) => (
                  <button key={m} type="button" disabled={!canWrite} onClick={() => save("po_sync_shape", m)}
                          className={`px-2.5 h-8 text-xs border-r last:border-r-0 border-slate-200 ${values.po_sync_shape === m ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                          data-testid={`fs-po-sync-shape-${m}`}>{lbl}</button>
                ))}
              </div>
            </Row>
          )}
          {["po_div_level_1", "po_div_level_2", "po_div_level_3"].filter(k => spec[k]).map(k => (
            <Row key={k} label={spec[k].label} note={spec[k].note} testId={`fs-row-${k}`}>
              <Input value={draft[k] ?? ""} onChange={e => setDraft(d => ({ ...d, [k]: e.target.value }))} disabled={!canWrite}
                     className="h-8 w-20 font-mono text-xs" inputMode="numeric" data-testid={`fs-input-${k}`} />
              <Button size="sm" variant="outline" className="h-8" disabled={!canWrite || saving === k || String(draft[k]) === String(values[k])}
                      onClick={() => save(k, draft[k])} data-testid={`fs-save-${k}`}>
                {saving === k ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              </Button>
            </Row>
          ))}
          {spec.co_line_type && (
            <Row label={spec.co_line_type.label} note={spec.co_line_type.note} testId="fs-row-co_line_type">
              <span className="inline-flex items-center h-8 px-2.5 rounded-md border border-slate-200 bg-slate-50 font-mono text-xs text-slate-700" data-testid="fs-co-line-type">
                H header + B budget lines
              </span>
            </Row>
          )}
        </section>

        <FoundationARSettings spec={spec} values={values} draft={draft} setDraft={setDraft} save={save} saving={saving} canWrite={canWrite} />

        <section>
          <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1">Reference table names</div>
          <div className="text-[11px] text-slate-500 mb-1">Override only if a Foundation table name is wrong. Blank = default.</div>
          {refTables.map(l => (
            <Row key={l.list_id} label={l.list_id} note={`default: ${l.default_table}`} testId={`fs-row-ref-${l.list_id}`}>
              <Input defaultValue={l.table !== l.default_table ? l.table : ""} placeholder={l.default_table} disabled={!canWrite}
                     className="h-8 w-56 font-mono text-xs" data-testid={`fs-ref-table-${l.list_id}`}
                     onBlur={e => { const v = e.target.value.trim(); const cur = (values.ref_tables || {})[l.list_id] || ""; if (v !== cur) save("ref_tables", { ...(values.ref_tables || {}), [l.list_id]: v }); }} />
            </Row>
          ))}
        </section>

        <section>
          <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1">Change log</div>
          <div className="rounded-md border border-slate-200 max-h-56 overflow-y-auto text-[11px]" data-testid="fs-audit">
            {audit.length === 0 && <div className="p-2 text-slate-400">No changes yet.</div>}
            {audit.map((a, i) => (
              <div key={i} className="flex gap-2 px-2 py-1 border-b border-slate-100 last:border-b-0">
                <span className="text-slate-400 w-32 shrink-0">{String(a.at).replace("T", " ").slice(0, 16)}</span>
                <span className="text-slate-600 w-40 shrink-0 truncate">{a.by}</span>
                <span className="font-mono text-slate-700 truncate">{a.kind === "mapping" ? `${a.map}:${a.label || a.key}` : a.key}</span>
                <span className="text-slate-500 truncate">{JSON.stringify(a.old)} → <strong>{JSON.stringify(a.new)}</strong></span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
